import { Stack } from '@mui/material';
import { SetStateAction, useCallback, useMemo, useState } from 'react';

import { _getAdminViewsFundsInfinite } from '@/api/admin/infinite-queries';
import { AdminFundsSortField } from '@/api/admin/types';
import { FundApprovalStatus, InvestmentAvailabilityStatusType } from '@/api/fund/types';
import { SortDirection, SortDirectionType } from '@/api/types';
import AddIcon from '@/assets/svg/ui-elements/plus.svg';
import { Button, Spinner, Tooltip, Typography } from '@/design-system';
import useDebounce from '@/global-hooks/use-debounce';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { useUrlSyncedState } from '@/global-hooks/use-url-synced-state';
import { useInfiniteScroll } from '@/shared/hooks/use-infinite-scroll';
import { DeniedReasonTrigger, Permission, useCan } from '@/shared/modules/permissions';

import { FundsGalleryView } from './components/funds-gallery-view';
import { FundsListView } from './components/funds-list-view';
import { FundsTabFilters, FundsTabFiltersValue } from './components/funds-tab-filters';

const URL_KEYS = [
  'status',
  'legalStructure',
  'investmentAvailabilityStatus',
  'search',
  'sortBy',
  'sortDirection',
  'viewTab',
] as const;

export const CREATE_FUND_BLOCKED_TOOLTIP = 'You do not have permission to create a fund';

export const FundsTab = () => {
  const { handleOpen } = useGlobalModal('CREATE_NEW_FUND_MODAL');
  const canCreateFund = useCan(Permission.FundManage);

  const {
    state: urlState,
    setMany,
    sortBy: sortByRaw,
    sortDirection: sortDirectionRaw,
  } = useUrlSyncedState(URL_KEYS);

  const activeView = urlState.viewTab?.[0] === 'gallery' ? 'gallery' : 'list';

  const setActiveView = useCallback(
    (next: 'list' | 'gallery') => {
      setMany({ viewTab: next === 'list' ? undefined : next });
    },
    [setMany],
  );

  const sortBy = (sortByRaw as AdminFundsSortField) || null;
  const sortDirection = (sortDirectionRaw as SortDirectionType) || null;

  const [columns, setColumns] = useState<string[]>([
    'name',
    'entityName',
    'investorsCount',
    'tvl',
    'investmentAvailabilityStatus',
    'status',
    'updatedAt',
    'createdAt',
    'transactionsCount',
    'currency',
    'minInvestment',
    'legalStructure',
    'projectedYield',
    'category',
    'assets',
    'strategies',
    'maxFundCapacity',
    'fundInceptionDate',
    'navStrikeFrequency',
    'lockupPeriod',
    'redemptionFrequency',
    'reportingFrequency',
    'actions',
  ]);

  const filters: FundsTabFiltersValue = useMemo(
    () => ({
      search: urlState.search?.[0] ?? '',
      status: urlState.status ?? [],
      legalStructure: urlState.legalStructure ?? [],
      investmentAvailabilityStatus: urlState.investmentAvailabilityStatus ?? [],
      columns,
    }),
    [
      urlState.search,
      urlState.status,
      urlState.legalStructure,
      urlState.investmentAvailabilityStatus,
      columns,
    ],
  );

  const setFilters = useCallback(
    (action: SetStateAction<FundsTabFiltersValue>) => {
      const next = typeof action === 'function' ? action(filters) : action;
      setMany(
        {
          search: next.search || undefined,
          status: next.status,
          legalStructure: next.legalStructure,
          investmentAvailabilityStatus: next.investmentAvailabilityStatus,
        },
        true,
      );
      setColumns(next.columns);
    },
    [filters, setMany],
  );

  const debouncedSearch = useDebounce(filters.search, 500);

  const { items, isLoading, isFetchingNextPage, loadMoreRef } = useInfiniteScroll(
    _getAdminViewsFundsInfinite({
      search: debouncedSearch?.length > 1 ? debouncedSearch : undefined,
      sortBy: sortBy || undefined,
      sortDirection: sortDirection ? (sortDirection.toLowerCase() as 'asc' | 'desc') : undefined,
      status: filters.status.length ? (filters.status as FundApprovalStatus[]) : undefined,
      legalStructure: filters.legalStructure.length ? filters.legalStructure : undefined,
      investmentAvailabilityStatus: filters.investmentAvailabilityStatus.length
        ? (filters.investmentAvailabilityStatus as InvestmentAvailabilityStatusType[])
        : undefined,
    }),
    (page) => page.items,
  );

  const handleSort = useCallback(
    (key: string) => {
      if (sortBy !== key) {
        setMany({ sortBy: key, sortDirection: SortDirection.ASC });
      } else if (sortDirection === SortDirection.ASC) {
        setMany({ sortDirection: SortDirection.DESC });
      } else {
        setMany({ sortBy: undefined, sortDirection: undefined });
      }
    },
    [sortBy, sortDirection, setMany],
  );

  return (
    <Stack flexDirection="column" alignItems="center" mt="16px" gap="16px">
      <Stack
        width="100%"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap="12px"
      >
        <Typography variant="h5">Funds Summary</Typography>

        <Tooltip title={canCreateFund ? '' : CREATE_FUND_BLOCKED_TOOLTIP}>
          <DeniedReasonTrigger denied={!canCreateFund}>
            <Button
              color="secondary"
              variant="solid"
              disabled={!canCreateFund}
              onClick={handleOpen}
            >
              <AddIcon />
              Create New Fund
            </Button>
          </DeniedReasonTrigger>
        </Tooltip>
      </Stack>

      <FundsTabFilters
        value={filters}
        setValue={setFilters}
        activeView={activeView}
        setActiveView={setActiveView}
      />

      {activeView === 'list' ? (
        <FundsListView
          items={items}
          isLoading={isLoading}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={handleSort}
          visibleColumns={columns}
        />
      ) : (
        <Stack
          display="grid"
          gridTemplateColumns="repeat(auto-fill, minmax(360px, 1fr))"
          rowGap="24px"
          columnGap="12px"
          width="100%"
        >
          <FundsGalleryView items={items} isLoading={isLoading} />
        </Stack>
      )}

      <div ref={loadMoreRef} style={{ height: '20px' }} />

      {isFetchingNextPage && (
        <Stack padding="16px" alignItems="center" justifyContent="center">
          <Spinner />
        </Stack>
      )}
    </Stack>
  );
};
